import { useCallback, useEffect, useState } from 'react';
import {
  buildCreateProfilePayload,
  buildUpdateProfilePayload,
  profileToFormValues,
} from '../utils/normalizeProfile';
import { buildDefaultProfileFormValues } from '../utils/profileFormAccessUtils';
import { validateProfileForm, hasValidationErrors } from '../utils/profileValidation';
import { profileService } from '../services/profileService';

export default function useProfileForm({
  profile = null,
  onSuccess,
  onClose,
  open = false,
  loggedInUser = null,
  showIsStandard = false,
}) {
  const isEditMode = Boolean(profile?.id);
  const [form, setForm] = useState(() =>
    profile ? profileToFormValues(profile) : buildDefaultProfileFormValues(loggedInUser)
  );
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [validationAttempt, setValidationAttempt] = useState(0);

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(
      profile
        ? profileToFormValues(profile)
        : buildDefaultProfileFormValues(loggedInUser)
    );
    setErrors({});
    setIsDirty(false);
    setValidationAttempt(0);
  }, [loggedInUser, open, profile]);

  const resetForm = useCallback(() => {
    setForm(
      profile
        ? profileToFormValues(profile)
        : buildDefaultProfileFormValues(loggedInUser)
    );
    setErrors({});
    setIsDirty(false);
    setValidationAttempt(0);
  }, [loggedInUser, profile]);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose?.();
  }, [onClose, resetForm]);

  const submit = useCallback(async () => {
    const validationErrors = validateProfileForm(form);
    setErrors(validationErrors);

    if (hasValidationErrors(validationErrors)) {
      setValidationAttempt((attempt) => attempt + 1);
      return { success: false, validationFailed: true };
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        const payload = buildUpdateProfilePayload(form, profile.id, { showIsStandard });
        const res = await profileService.updateProfile(profile.id, payload);
        if (!res.ok) {
          return { success: false, message: 'Failed to update profile.' };
        }
        onSuccess?.(res.data, { isEditMode: true });
        handleClose();
        return { success: true, data: res.data };
      }

      const payload = buildCreateProfilePayload(form, { showIsStandard });
      const createRes = await profileService.createProfile(payload);

      if (!createRes.ok || !createRes.data?.id) {
        return { success: false, message: 'Failed to create profile.' };
      }

      onSuccess?.(createRes.data, { isEditMode: false });
      handleClose();
      return { success: true, data: createRes.data, redirectToPermissions: true };
    } catch (error) {
      return {
        success: false,
        message: error?.message || 'Something went wrong while saving the profile.',
      };
    } finally {
      setIsSubmitting(false);
    }
  }, [form, handleClose, isEditMode, onSuccess, profile?.id, showIsStandard]);

  return {
    form,
    errors,
    isSubmitting,
    isEditMode,
    isDirty,
    validationAttempt,
    updateField,
    submit,
    resetForm,
    handleClose,
  };
}
